import { FunctionComponent, useEffect } from 'react';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { MultiStepFormProvider } from '../../../providers/MultiStepForm.provider';
import { usePendingData } from '../../../providers/PendingData.provider';
import { usePartnerAccountBusinessCase } from '../../../providers/PartnerAccountsBusinessCase.provider';
import { useHubSpotData } from '../../../providers/HubSpotData.provider';
import AccountCreationForm from './AccountCreationForm';
import AccountCreationContext from './AccountCreationContext';

import usePostPartnerAccountBusinessCaseDialogue from '../../../hooks/useDialogues/usePostPartnerAccountBusinessCase.dialogue';

import type { AccountsHandlers } from '../types';
import { usePartnerLogin } from '../../../providers/PartnerLogin.provider';
import { useDocument } from '../../../providers/Document.provider';
import { usePartnerAccountMatrix } from '../../../providers/PartnerAccountsMatrix.provider';
import useAccountCreationFields from '@/hooks/useFields/useAccountCreationFields';

type AccountCreationContainerProps = {
  handlers: AccountsHandlers;
};

type FC = FunctionComponent<AccountCreationContainerProps>;

const AccountCreationContainer: FC = ({ handlers }: AccountCreationContainerProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'AccountCreationContainer' });
  const { hasPendingData } = usePendingData();
  const { response } = usePartnerAccountBusinessCase();
  const { response: hubSpotResponse } = useHubSpotData();
  const { response: accountMatrixResponse } = usePartnerAccountMatrix();
  const { response: documentUploadResponse } = useDocument();
  const { destination, navigate, showDialogue } = usePostPartnerAccountBusinessCaseDialogue();
  const { response: partnerLoginResponse } = usePartnerLogin();
  const { isInsideSales } = partnerLoginResponse;

  const fields = useAccountCreationFields();
  useEffect(() => {
    let checkHubSpot: string | boolean = isInsideSales;
    let checkDocumentUpload: string | boolean = isInsideSales;
    let checkAccountMatrix: string | boolean = isInsideSales;
    if (checkHubSpot) {
      checkHubSpot = (hubSpotResponse?.success || hubSpotResponse?.errorMessage) ?? false;
    }
    if (checkAccountMatrix) {
      checkAccountMatrix = (accountMatrixResponse?.success || accountMatrixResponse?.errorMessage) ?? false;
    }
    if (checkDocumentUpload) {
      checkDocumentUpload = (documentUploadResponse?.success || documentUploadResponse?.errorMessage) ?? false;
    }
    if (response?.success || response?.errorMessage || checkHubSpot || checkAccountMatrix || checkDocumentUpload)
      return showDialogue();
    return () => {};
  }, [response, hubSpotResponse, accountMatrixResponse]);

  useEffect(() => {
    navigate();
  }, [destination]);

  return (
    <div>
      <MultiStepFormProvider context={AccountCreationContext} fields={fields} onSubmit={handlers.onAccountCreation}>
        <AccountCreationForm />
      </MultiStepFormProvider>

      <Prompt
        when={hasPendingData}
        message={t('unsavedDataPrompt', { defaultValue: 'You have unsaved account data that will be discarded.' })}
      />
    </div>
  );
};

export default AccountCreationContainer;
